import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#initSimpleZoom(signal);
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Hover zoom with transform-origin following the cursor (simple zoom).
   * @param {AbortSignal} signal
   */
  #initSimpleZoom(signal) {
    if (!this.classList.contains('media-gallery--simple-zoom')) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    for (const container of this.querySelectorAll('.product-media-container--simple-zoom')) {
      const image = container.querySelector('.product-media__image');
      if (!image) continue;

      /** @type {number | null} */
      let frame = null;

      const updateZoom = (clientX, clientY) => {
        const intensity =
          parseFloat(getComputedStyle(this).getPropertyValue('--simple-zoom-intensity').trim()) || 100;
        const scale = 1 + intensity / 100;
        const rect = container.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) return;

        const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

        image.style.transformOrigin = `${x}% ${y}%`;
        image.style.transform = `scale(${scale})`;
      };

      const resetZoom = () => {
        if (frame) {
          cancelAnimationFrame(frame);
          frame = null;
        }

        image.classList.remove('is-simple-zoom-active');
        image.style.transform = '';
        image.style.transformOrigin = '';
      };

      container.addEventListener(
        'mouseenter',
        (event) => {
          image.classList.add('is-simple-zoom-active');
          updateZoom(event.clientX, event.clientY);
        },
        { signal }
      );

      container.addEventListener(
        'mousemove',
        (event) => {
          const { clientX, clientY } = event;

          if (frame) return;

          frame = requestAnimationFrame(() => {
            frame = null;
            updateZoom(clientX, clientY);
          });
        },
        { signal, passive: true }
      );

      container.addEventListener('mouseleave', resetZoom, { signal });
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // Replace the element
    this.replaceWith(newMediaGallery);

    // If the new gallery has animation classes, ensure it's visible immediately
    // (since it's already on screen when variant changes)
    if (newMediaGallery.classList.contains('media-gallery--animated')) {
      // Force immediate visibility for variant updates
      newMediaGallery.classList.add('is-visible');
      
      // Also reinitialize animation system if needed
      const animationSelector = newMediaGallery.className.match(/media-gallery-animated-[\w-]+/);
      if (animationSelector && window.AnimationSystem) {
        setTimeout(() => {
          window.AnimationSystem.init(`.${animationSelector[0]}`, {
            animatedClass: 'media-gallery--animated'
          });
        }, 10);
      }
    }

    // Reinitialize cascade animation for thumbnails if needed
    this.#reinitializeCascadeAnimation(newMediaGallery);

    // Reinitialize thumbnail navigation arrows if needed
    this.#reinitializeThumbnailNavigation(newMediaGallery);

    // Filter images by color if enabled
    this.#filterImagesByColor(newMediaGallery);
  };

  /**
   * Reinitializes cascade animation for thumbnails after variant update.
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #reinitializeCascadeAnimation(gallery) {
    // Check if cascade animation is enabled via data attribute
    const cascadeEnabled = gallery.dataset.cascadeEnabled === 'true';
    if (!cascadeEnabled) return;

    // Check if there are thumbnails that need cascade animation
    const thumbnails = gallery.querySelectorAll('.slideshow-control');
    if (thumbnails.length === 0) return;

    // Get cascade animation settings from data attributes
    const cascadeConfig = {
      cascadeType: gallery.dataset.cascadeType || 'fade-in',
      cascadeDuration: parseInt(gallery.dataset.cascadeDuration, 10) || 600,
      cascadeInitialDelay: parseInt(gallery.dataset.cascadeInitialDelay, 10) || 500,
      cascadeStagger: parseInt(gallery.dataset.cascadeStagger, 10) || 200
    };

    // Reset thumbnails
    thumbnails.forEach(thumb => {
      thumb.classList.remove('thumbnail-cascade-visible');
      thumb.style.animationDelay = '';
      thumb.style.opacity = '0';
    });

    // Get visible thumbnails
    const visibleThumbnails = Array.from(thumbnails).filter(thumb => {
      const thumbStyles = window.getComputedStyle(thumb);
      if (thumbStyles.display === 'none' || thumbStyles.visibility === 'hidden') {
        return false;
      }
      
      let parent = thumb.parentElement;
      while (parent && parent !== gallery) {
        const styles = window.getComputedStyle(parent);
        if (styles.display === 'none') {
          return false;
        }
        parent = parent.parentElement;
      }
      
      return true;
    });

    if (visibleThumbnails.length === 0) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      visibleThumbnails.forEach(thumb => {
        thumb.style.opacity = '1';
      });
      return;
    }

    // Animate thumbnails with cascade effect
    visibleThumbnails.forEach((thumb, index) => {
      const delay = cascadeConfig.cascadeInitialDelay + (index * cascadeConfig.cascadeStagger);
      thumb.style.animationDelay = delay + 'ms';
      
      setTimeout(() => {
        thumb.classList.add('thumbnail-cascade-visible');
      }, 10);
    });
  }

  /**
   * Reinitializes thumbnail navigation arrows after variant update.
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #reinitializeThumbnailNavigation(gallery) {
    // Check if thumbnails are at bottom position (where navigation arrows are used)
    const galleryId = gallery.dataset.galleryId;
    if (!galleryId) return;

    const navContainer = gallery.querySelector(`.media-gallery-${galleryId}-thumbnails-nav`);
    if (!navContainer) return;

    const prevArrow = navContainer.querySelector('.thumbnail-nav-arrow--prev');
    const nextArrow = navContainer.querySelector('.thumbnail-nav-arrow--next');
    const thumbnailsContainer = navContainer.querySelector('.slideshow-controls__thumbnails-container');

    if (!prevArrow || !nextArrow || !thumbnailsContainer) return;

    // Remove old event listeners by cloning elements (clean slate)
    const newPrevArrow = prevArrow.cloneNode(true);
    const newNextArrow = nextArrow.cloneNode(true);
    prevArrow.replaceWith(newPrevArrow);
    nextArrow.replaceWith(newNextArrow);

    // Get settings from data attributes or use defaults
    const thumbnailWidth = parseInt(gallery.style.getPropertyValue('--thumbnail-width'), 10) || 65;
    const visibleCount = 5; // Default, could be made configurable

    function updateArrowVisibility() {
      const scrollLeft = thumbnailsContainer.scrollLeft;
      const scrollWidth = thumbnailsContainer.scrollWidth;
      const clientWidth = thumbnailsContainer.clientWidth;
      const scrollRight = scrollWidth - scrollLeft - clientWidth;

      // Show/hide prev arrow
      if (scrollLeft <= 1) {
        newPrevArrow.classList.add('hidden');
      } else {
        newPrevArrow.classList.remove('hidden');
      }

      // Show/hide next arrow
      if (scrollRight <= 1) {
        newNextArrow.classList.add('hidden');
      } else {
        newNextArrow.classList.remove('hidden');
      }
    }

    function scrollThumbnails(direction) {
      const thumbnailWidthWithGap = thumbnailWidth + 8; // width + gap
      const scrollAmount = thumbnailWidthWithGap * visibleCount;

      const currentScroll = thumbnailsContainer.scrollLeft;
      const targetScroll = direction === 'prev'
        ? Math.max(0, currentScroll - scrollAmount)
        : Math.min(thumbnailsContainer.scrollWidth - thumbnailsContainer.clientWidth, currentScroll + scrollAmount);

      thumbnailsContainer.scrollTo({
        left: targetScroll,
        behavior: 'smooth'
      });
    }

    // Add event listeners
    newPrevArrow.addEventListener('click', () => scrollThumbnails('prev'));
    newNextArrow.addEventListener('click', () => scrollThumbnails('next'));
    thumbnailsContainer.addEventListener('scroll', updateArrowVisibility);

    // Initial check
    setTimeout(() => {
      updateArrowVisibility();
    }, 100);
  }

  /**
   * Filters images by color based on selected variant color option.
   * Shows only media whose alt text or filename contains the selected color
   * (e.g. selecting "preto" keeps "ciclista-flashback-preto-1").
   * @param {HTMLElement} gallery - The new media gallery element.
   */
  #filterImagesByColor(gallery) {
    const filterByColor = gallery.dataset.filterByColor === 'true';
    if (!filterByColor) return;

    // Fonte da cor para os thumbnails (as mídias principais já vêm com data-media-color do servidor)
    const colorSource = gallery.dataset.filterColorSource || 'alt';

    // Get selected color from variant picker
    const variantPicker = gallery.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return;

    // Find color option (usually option1, but can vary)
    const colorOption = Array.from(variantPicker.querySelectorAll('fieldset')).find(fieldset => {
      const legend = fieldset.querySelector('legend');
      if (!legend) return false;
      const optionName = legend.textContent?.toLowerCase() || '';
      return optionName.includes('cor') || optionName.includes('color');
    });

    if (!colorOption) return;

    // Get selected color value
    const selectedInput = colorOption.querySelector('input:checked, input[checked]');
    if (!selectedInput) return;

    // Normaliza texto igual ao snippet Liquid: minúsculas, sem extensão, separadores -> espaço,
    // acentos removidos (ex.: "AÇAÍ" → "acai", como o Shopify grava no nome do arquivo).
    // Em modo filename, "/" (e "\") vira espaço — no arquivo costuma ser hífen
    // (ex.: opção "PRETO/BRANCO" → "short-vibe-ng-preto-branco-0").
    const normalize = (text, { forFilename = false } = {}) => {
      let value = (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\.(jpe?g|png|webp|gif|avif)\b/gi, '');
      if (forFilename) {
        value = value.replace(/[/\\]+/g, ' ');
      }
      return value
        .replace(/[-_.]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const useFilename = colorSource === 'filename';
    const selectedColor = normalize(selectedInput.value, { forFilename: useFilename });
    if (!selectedColor) return;

    // Deriva o nome do arquivo a partir de uma URL de imagem.
    const getFilename = (src) => (src || '').split('?')[0].split('/').pop() || '';

    // Casa por palavra inteira (padding com espaços) para evitar falsos positivos.
    const colorNeedle = ` ${selectedColor} `;
    const matchesColor = (text) => ` ${normalize(text, { forFilename: useFilename })} `.includes(colorNeedle);

    const setVisible = (element, visible) => {
      element.style.display = visible ? '' : 'none';
      if (visible) {
        element.removeAttribute('hidden');
      } else {
        element.setAttribute('hidden', 'true');
      }
      if (element instanceof HTMLElement) element.hidden = !visible;
    };

    // Filter all media elements (slides, grid items, carousel-extended slides)
    const allMediaElements = gallery.querySelectorAll(
      'slideshow-slide[data-media-color], li[data-media-color], .carousel-extended__slide[data-media-color]'
    );
    allMediaElements.forEach(mediaElement => setVisible(mediaElement, matchesColor(mediaElement.dataset.mediaColor)));

    // Also filter thumbnails if they exist
    const thumbnails = gallery.querySelectorAll('.slideshow-control');
    thumbnails.forEach(thumb => {
      const thumbImg = thumb.querySelector('img');
      if (!thumbImg) return;

      const thumbAlt = thumbImg.getAttribute('alt') || '';
      let thumbText = thumbAlt;
      if (useFilename) {
        thumbText = getFilename(thumbImg.getAttribute('src'));
        // Vídeo/3D: a Shopify não expõe o nome original do arquivo; paridade com snippets/media-color-source.liquid.
        if (thumb.querySelector('.slideshow-controls__thumbnail-badge')) thumbText += ` ${thumbAlt}`;
      }
      setVisible(thumb, matchesColor(thumbText));
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
